/**
 * Minimap — the always-on small map in the top-right corner, with the stat
 * indicator row (MatchStats) hanging directly off its bottom edge.
 *
 * create(hud) builds the PIXI objects into hud.elements.minimap, position(hud)
 * lays them out for hud.screenWidth/Height, update(hud, …) redraws from game
 * state. `hud` is the PixiHUDRenderer coordinator.
 *
 * Shows a `worldRadius` circle of world around the local player, north-up:
 * the arena's flat objects (walls/blocks/pillars/firewalls) redrawn from
 * gameState.worldObjects at minimap scale, the storm's current and next safe
 * squares, and the markers chosen by minimapMath.collectMarkers.
 *
 * Cost model: the world layer is tessellated ONCE in world-scaled space
 * (view = {scale, tx:0, ty:0}) and then merely translated each frame as the
 * player moves. The zone layer is redrawn whenever its dirty key changes —
 * which, during a storm shrink, is every frame by design (GameState.zoneView()
 * interpolates the boundary continuously); only during a hold does it settle
 * back to being translate-only, same as the world layer. Only the marker
 * layer (a dozen circles) is unconditionally rebuilt per frame. The drawing
 * routines are exported so MapOverlay reuses them with a fit-to-screen view.
 */
import * as PIXI from 'pixi.js';
import { changed, invalidate } from './dirty.js';
import { outlineOf, styleFor, getWorldTheme } from '../worldTheme.js';
import {
  MINIMAP_DEFAULTS, smallMapView, project, scalePoints, zoneBounds, objectsKey, collectMarkers, isMinimapObject
} from './minimapMath.js';

export const PANEL_MARGIN = 30;
const ZONE_COLOR = 0x72ffb8; // matches the world's safe-zone border and ZonePanel

/** Effective tuning: config/ui.json `minimap` over the code defaults. */
export function minimapConfig(callbacks) {
  return { ...MINIMAP_DEFAULTS, ...(callbacks?.uiConfig?.minimap || {}) };
}

export function create(hud) {
  const container = new PIXI.Container();
  container.zIndex = 50;
  container.visible = false;
  container.eventMode = 'none';

  const panelBg = new PIXI.Graphics();
  const maskG = new PIXI.Graphics();
  const clip = new PIXI.Container();
  const objectsG = new PIXI.Graphics();
  const zoneG = new PIXI.Graphics();
  const markersG = new PIXI.Graphics();
  const frame = new PIXI.Graphics();

  clip.addChild(objectsG, zoneG, markersG);
  clip.mask = maskG; // world 1500 px away spills past the panel; the mask clips it
  container.addChild(panelBg, maskG, clip, frame);

  hud.hudContainer.addChild(container);
  hud.hudContainer.sortableChildren = true;

  hud.elements.minimap = {
    container, panelBg, maskG, clip, objectsG, zoneG, markersG, frame,
    size: MINIMAP_DEFAULTS.panelSize
  };
  position(hud);
}

export function position(hud) {
  const el = hud.elements.minimap;
  if (!el) return;
  const s = el.size;

  el.container.x = hud.screenWidth - s - PANEL_MARGIN;
  el.container.y = PANEL_MARGIN;

  el.panelBg.clear();
  el.panelBg.roundRect(0, 0, s, s, 5);
  el.panelBg.fill({ color: 0x000000, alpha: 0.675 });

  el.maskG.clear();
  el.maskG.roundRect(0, 0, s, s, 5);
  el.maskG.fill({ color: 0xffffff });

  el.frame.clear();
  el.frame.roundRect(0, 0, s, s, 5);
  el.frame.stroke({ width: 2, color: 0xffffff, alpha: 0.6 });

  invalidate(el);
}

export function update(hud, gameState, callbacks) {
  const el = hud.elements.minimap;
  if (!el) return;

  const local = gameState.players.get(gameState.localId);
  const visible = Boolean(local) && !gameState.mapOpen;
  el.container.visible = visible;
  if (!visible) return;

  const cfg = minimapConfig(callbacks);
  if (el.size !== cfg.panelSize) {
    el.size = cfg.panelSize;
    position(hud);
  }

  const mapW = gameState.mapWidth || 0;
  const mapH = gameState.mapHeight || 0;
  const view = smallMapView(local.x, local.y, el.size, cfg.worldRadius);
  const worldView = { scale: view.scale, tx: 0, ty: 0 };

  if (changed(el, 'objects', objectsKey(gameState.worldObjectsVersion, mapW, mapH, view.scale))) {
    el.objectsG.clear();
    drawWorld(el.objectsG, gameState.worldObjects, worldView, mapW, mapH);
  }

  // The interpolated view (GameState.zoneView()), so the minimap's wall glides in step
  // with the world renderer instead of stepping once a second beside it.
  const zs = typeof gameState.zoneView === 'function' ? gameState.zoneView() : gameState.zoneState;
  if (changed(el, 'zone', { shape: zs?.shape ?? null, target: zs?.targetShape ?? null, scale: view.scale })) {
    el.zoneG.clear();
    drawZone(el.zoneG, zs, worldView);
  }

  // Pan by transform: no re-tessellation as the player moves.
  el.objectsG.position.set(view.tx, view.ty);
  el.zoneG.position.set(view.tx, view.ty);

  el.markersG.clear();
  drawMarkers(el.markersG, gameState, view, performance.now(), cfg.shotMarkerMs);
}

// ─────────────────────────────────────────────────────────────
// Shared drawing routines (also used by MapOverlay)
// ─────────────────────────────────────────────────────────────

/** Ground tint, every flat world object at minimap scale, then the arena boundary. */
export function drawWorld(g, worldObjects, view, mapW, mapH) {
  const theme = getWorldTheme();
  const hasMap = mapW > 0 && mapH > 0;
  const origin = project(view, 0, 0);

  if (hasMap) {
    g.rect(origin.x, origin.y, mapW * view.scale, mapH * view.scale);
    g.fill({ color: theme.ground, alpha: 0.35 });
  }

  if (worldObjects) {
    for (const obj of worldObjects.values()) {
      if (!isMinimapObject(obj)) continue;
      const pts = outlineOf(obj);
      if (!pts) continue;
      const style = styleFor(obj);
      const health = obj.maxHp > 0 ? Math.max(0, Math.min(1, obj.hp / obj.maxHp)) : 1;
      g.poly(scalePoints(view, pts));
      g.fill({ color: style.fill, alpha: (style.alpha ?? 1) * (0.4 + 0.6 * health) });
    }
  }

  if (hasMap) {
    g.rect(origin.x, origin.y, mapW * view.scale, mapH * view.scale);
    g.stroke({ width: 2, color: theme.boundary, alpha: 1 });
  }
}

/** Current safe square (bright) and the next target square (dim). */
export function drawZone(g, zoneState, view) {
  const next = zoneBounds(zoneState?.targetShape);
  if (next) strokeBounds(g, next, view, { width: 1, color: ZONE_COLOR, alpha: 0.45 });
  const cur = zoneBounds(zoneState?.shape);
  if (cur) strokeBounds(g, cur, view, { width: 2, color: ZONE_COLOR, alpha: 0.9 });
}

function strokeBounds(g, b, view, style) {
  const tl = project(view, b.minX, b.minY);
  g.rect(tl.x, tl.y, (b.maxX - b.minX) * view.scale, (b.maxY - b.minY) * view.scale);
  g.stroke(style);
}

/** Teammates, enemy-fire markers, then the local player on top. */
export function drawMarkers(g, gameState, view, nowMs, windowMs) {
  for (const m of collectMarkers(gameState, nowMs, windowMs)) {
    const p = project(view, m.x, m.y);
    g.circle(p.x, p.y, m.radius);
    g.fill({ color: m.color, alpha: m.alpha });
    if (m.kind === 'local') g.stroke({ width: 1, color: 0xffffff, alpha: 0.9 });
  }
}
